var m = require('mithril');
var prop = require('mithril/stream');
var edit = require('../../layout/components/edit');
var loaders = require('../../layout/components/loaders');
var heading = require('../../components/heading');
var pointTd = require('../../data/components/pointTd');
var Sortable = require('../../libs/draggableWithDragscroll');
var helpModal = require('../../help/helpModal');
var getEquipmentLocationRid = require('../utils/getEquipmentLocationRid');
// Equipment picker is now part of the element edit modal

var smartList = {
  oninit: function (vnode) {
    var ctrl = this;
    ctrl.element = vnode.attrs.element; // function that returns element record
    ctrl.parentReinitialize = vnode.attrs.parentReinitialize;
    ctrl.dashboardCallback = vnode.attrs.dashboardCallback;

    ctrl.loading = prop(true);
    ctrl.error = prop(null);
    ctrl.datasets = prop([]);
    ctrl.targetEquipment = prop(null); // store fetched equipment for title link
    ctrl.equipmentLocationRid = null;

    ctrl.hiddenDatasets = prop(new Set()); // Set of dataset keys
    ctrl.hiddenPoints = prop({}); // { [datasetKey]: Set(pointName) }
    ctrl.order = prop({ datasets: [], points: {} });

    function getDatasetKey(ds) {
      return ds['@rid'] || ds.rid || (ds.name + '::' + (ds['@class'] || 'Dataset'));
    }

    function normalizeRid(value) {
      if (!value) return null;
      const rid = value.toString ? value.toString() : String(value);
      return rid.charAt(0) === '#' ? rid : '#' + rid;
    }

    function recordRid(record) {
      return normalizeRid(record && (record.rid || record['@rid']));
    }

    function findRecordByRid(records, rid) {
      const expectedRid = normalizeRid(rid);
      return (Array.isArray(records) ? records : []).find(record => {
        return recordRid(record) === expectedRid;
      }) || null;
    }

    function getDatasetPointNames(dataset) {
      const defNamesRaw = Array.isArray(dataset.columns) && dataset.columns.length
        ? dataset.columns.map(c => c.name)
        : (dataset.out_Column || []).map(c => c.name);
      return ctrl.uniqueStable(defNamesRaw).filter(Boolean);
    }

    function pruneHiddenStateToAvailable() {
      const availableDatasets = new Set(ctrl.datasets().map(dataset => ctrl.getDatasetKey(dataset)));
      const availablePoints = {};
      ctrl.datasets().forEach(dataset => {
        availablePoints[ctrl.getDatasetKey(dataset)] = new Set(getDatasetPointNames(dataset));
      });

      ctrl.hiddenDatasets(new Set(Array.from(ctrl.hiddenDatasets()).filter(key => availableDatasets.has(key))));
      ctrl.hiddenPoints(Object.fromEntries(
        Object.entries(ctrl.hiddenPoints())
          .filter(([key]) => availableDatasets.has(key))
          .map(([key, set]) => [key, new Set(Array.from(set).filter(pointName => availablePoints[key] && availablePoints[key].has(pointName)))])
      ));
    }

    function pruneOrderToAvailable() {
      const currentOrder = ctrl.order() || { datasets: [], points: {} };
      const availableDatasets = new Set(ctrl.datasets().map(dataset => ctrl.getDatasetKey(dataset)));
      const availablePoints = {};
      ctrl.datasets().forEach(dataset => {
        availablePoints[ctrl.getDatasetKey(dataset)] = new Set(getDatasetPointNames(dataset));
      });
      ctrl.order({
        datasets: (currentOrder.datasets || []).filter(key => availableDatasets.has(key)),
        points: Object.fromEntries(
          Object.entries(currentOrder.points || {})
            .filter(([key]) => availableDatasets.has(key))
            .map(([key, list]) => [
              key,
              (Array.isArray(list) ? list : []).filter(pointName => availablePoints[key] && availablePoints[key].has(pointName))
            ])
        )
      });
    }

    function datasetsFromFieldDiscovery(fieldDiscovery) {
      const datasets = fieldDiscovery && Array.isArray(fieldDiscovery.datasets)
        ? fieldDiscovery.datasets
        : [];
      return datasets
        .filter(dataset => Number(dataset.componentDepth) === 0)
        .filter(dataset => (dataset.type ? dataset.type === 'list' : true))
        .map(dataset => {
          const points = Array.isArray(dataset.points) ? dataset.points : [];
          return {
            '@rid': dataset.rid,
            rid: dataset.rid,
            '@class': 'Dataset',
            name: dataset.name || dataset.datasetName,
            type: dataset.type,
            showInSummary: dataset.showInSummary,
            equipmentRid: dataset.equipmentRid,
            equipmentName: dataset.equipmentName,
            columns: points.map((point, index) => ({ name: point.name, id: index + 1 })),
            out_Column: points.map(point => ({
              '@rid': point.columnRid,
              name: point.name,
              in: point.point || {
                '@rid': point.pointRid,
                name: point.name
              }
            }))
          };
        });
    }

    function filterDatasetsByEquipmentRid(datasets, rid) {
      const expectedRid = normalizeRid(rid);
      return (Array.isArray(datasets) ? datasets : []).filter(dataset => {
        return !dataset.equipmentRid || normalizeRid(dataset.equipmentRid) === expectedRid;
      });
    }

    function parseConfigObject(value) {
      if (!value) return {};
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return parsed && typeof parsed === 'object' ? parsed : {};
        } catch (e) {
          return {};
        }
      }
      return typeof value === 'object' ? value : {};
    }

    // Return a new array with duplicates removed, preserving first occurrence order
    ctrl.uniqueStable = function (arr) {
      if (!Array.isArray(arr)) return [];
      const seen = new Set();
      const out = [];
      for (const v of arr) {
        if (!seen.has(v)) { seen.add(v); out.push(v); }
      }
      return out;
    }

    async function saveHiddenConfig() {
      const el = ctrl.element();
      const rid = el['@rid'];
      pruneHiddenStateToAvailable();
      const hidden = Object.assign({}, parseConfigObject(el.hiddenFields));
      hidden._hiddenDatasets = Array.from(ctrl.hiddenDatasets());
      hidden._hiddenPoints = Object.fromEntries(
        Object.entries(ctrl.hiddenPoints()).map(([k, set]) => [k, Array.from(set)])
      );
      el.hiddenFields = hidden;

      await loaders.requestWithLoader({
        method: 'PUT',
        url: `/api/v1/element/${rid.slice(1)}`,
        body: { hiddenFields: hidden }
      });
    }

    async function saveOrder() {
      const el = ctrl.element();
      const rid = el['@rid'];
      pruneOrderToAvailable();
      const order = ctrl.order();
      el.smartListOrder = order;
      await loaders.requestWithLoader({
        method: 'PUT',
        url: `/api/v1/element/${rid.slice(1)}`,
        body: { smartListOrder: order }
      });
    }

    // expose for view handlers
    ctrl.saveOrder = saveOrder;

    ctrl.toggleHideDataset = async function (dataset) {
      const key = getDatasetKey(dataset);
      const ds = ctrl.hiddenDatasets();
      if (ds.has(key)) ds.delete(key); else ds.add(key);
      await saveHiddenConfig();
      m.redraw();
    };

    ctrl.toggleHidePoint = async function (dataset, pointName) {
      const key = getDatasetKey(dataset);
      const hp = ctrl.hiddenPoints();
      if (!hp[key]) hp[key] = new Set();
      if (hp[key].has(pointName)) hp[key].delete(pointName); else hp[key].add(pointName);
      await saveHiddenConfig();
      m.redraw();
    };

    async function init() {
      ctrl.loading(true);
      ctrl.error(null);
      try {
        const el = ctrl.element();
        const customEquipment = (el.customEquipment || '').toString().trim();
        if (!customEquipment) {
          throw new Error('Missing customEquipment. Provide an equipment RID (e.g. #12:34).');
        }
        const firstRid = customEquipment.split(',')[0].trim();
        if (!firstRid || firstRid[0] !== '#') {
          throw new Error('Invalid customEquipment. Expected format: #<cluster>:<record>.');
        }

        // initialize hidden state from element.hiddenFields
        const hf = parseConfigObject(el.hiddenFields);
        ctrl.hiddenDatasets(new Set(hf._hiddenDatasets || []));
        ctrl.hiddenPoints(Object.fromEntries(
          Object.entries(hf._hiddenPoints || {}).map(([k, arr]) => [k, new Set(arr)])
        ));

        // initialize saved order (datasets + points)
        const savedOrder = parseConfigObject(el.smartListOrder);
        ctrl.order({
          datasets: Array.isArray(savedOrder.datasets) ? savedOrder.datasets.slice() : [],
          points: typeof savedOrder.points === 'object' && savedOrder.points ? { ...savedOrder.points } : {}
        });

        const equipmentLocationRid = getEquipmentLocationRid(el);
        if (!equipmentLocationRid) {
          throw new Error('Unable to determine location context for Smart List.');
        }
        ctrl.equipmentLocationRid = equipmentLocationRid;
        const locationRidFragment = equipmentLocationRid.charAt(0) === '#' ? equipmentLocationRid.slice(1) : equipmentLocationRid;

        let listDatasets = [];
        if (edit.vm.editing()) {
          const fieldDiscovery = await loaders.requestWithLoader({
            loader: 'background',
            background: true,
            url: '/api/v1/entities/field-discovery',
            method: 'GET',
            params: {
              locationRid: locationRidFragment,
              customEquipment: firstRid,
              componentDepthLimit: 0,
              summaryOnly: 0
            }
          });
          const equipment = findRecordByRid(fieldDiscovery && fieldDiscovery.equipment, firstRid);
          ctrl.targetEquipment(equipment);
          listDatasets = filterDatasetsByEquipmentRid(datasetsFromFieldDiscovery(fieldDiscovery), firstRid);
        } else {
          const equipmentList = await loaders.requestWithLoader({
            loader: 'main',
            url: '/api/v1/entities/search',
            method: 'GET',
            params: {
              include: 'details',
              locationRid: locationRidFragment,
              customEquipment: firstRid,
              componentDepthLimit: 0,
              summaryOnly: 0
            }
          });

          const equipment = findRecordByRid(equipmentList, firstRid);
          ctrl.targetEquipment(equipment);
          const outContains = equipment && Array.isArray(equipment.out_Contains) ? equipment.out_Contains : [];
          // Filter datasets of type 'list' (default to include when type is missing and columns exist)
          listDatasets = outContains
            .map(e => e.in)
            .filter(Boolean)
            .filter(ds => (ds.type ? ds.type === 'list' : true));
        }

        listDatasets.forEach(ds => {
          if (!Array.isArray(ds.out_Column)) ds.out_Column = [];
          if (!Array.isArray(ds.columns)) ds.columns = [];
        });

        // Apply saved dataset order
        const keyOf = (ds) => ctrl.getDatasetKey(ds);
        let datasets = listDatasets.slice();
        const dsOrder = ctrl.order().datasets || [];
        if (dsOrder.length) {
          const byKey = new Map(datasets.map(d => [keyOf(d), d]));
          const ordered = dsOrder.map(k => byKey.get(k)).filter(Boolean);
          const remaining = datasets.filter(d => !dsOrder.includes(keyOf(d)));
          datasets = ordered.concat(remaining);
        }

        // No mutation of ds.out_Column here; we will respect saved point order at render-time
        // based on dataset.columns definitions.
      
        ctrl.datasets(datasets);
      } catch (e) {
        ctrl.error(e && e.message ? e.message : 'Failed to load SmartList');
      } finally {
        ctrl.loading(false);
      }
    }

    // Equipment selection handled in element edit modal

    ctrl.getDatasetKey = getDatasetKey;
    ctrl.init = init;
    ctrl.editHandler = function () {
      init();
    };
    edit.on('toggle', ctrl.editHandler);
    init();
  },

  onremove: function () {
    if (this.editHandler) {
      edit.removeListener('toggle', this.editHandler);
    }
  },

  view: function (vnode) {
    var ctrl = this;
    const el = ctrl.element();
    const labelStyle = el.labelStyle || 'custom';
    const isEditing = edit.vm.editing();

    function computeLabel() {
      const fallback = el.name || 'Smart List';
      if (labelStyle === 'custom') return el.name || '';
      const firstDs = Array.isArray(ctrl.datasets()) && ctrl.datasets().length ? ctrl.datasets()[0] : null;
      const dsName = firstDs && firstDs.name ? firstDs.name : undefined;
      if (labelStyle === 'first_dataset_name') {
        // Avoid showing a generic first dataset name like "Default"
        if (dsName && dsName.toLowerCase() === 'default') return fallback;
        return dsName || fallback;
      }
      if (labelStyle === 'equipment_and_dataset') {
        const eq = ctrl.targetEquipment && ctrl.targetEquipment();
        const eqName = (eq && (eq.name || eq['@class'])) || 'Equipment';
        return (eqName && dsName) ? (eqName + ' – ' + dsName) : (dsName || eqName || fallback);
      }
      return fallback;
    }

    // Ensure heading in edit mode shows computed label without modifying heading component
    if (isEditing) {
      if (labelStyle !== 'custom') {
        if (typeof el._originalNameForSmartList === 'undefined') {
          el._originalNameForSmartList = el.name;
        }
        el.name = computeLabel();
      } else if (typeof el._originalNameForSmartList !== 'undefined') {
        el.name = el._originalNameForSmartList;
        delete el._originalNameForSmartList;
      }
    } else if (typeof el._originalNameForSmartList !== 'undefined') {
      // Restore when leaving edit mode
      el.name = el._originalNameForSmartList;
      delete el._originalNameForSmartList;
    }

    if (ctrl.loading()) {
      return m('div.f3', [
        m(heading, { label: computeLabel(), class: 'f1', element: el, parentReinitialize: ctrl.parentReinitialize, dashboardCallback: ctrl.dashboardCallback }),
        m('div.moon-gray', 'Loading...')
      ]);
    }

    if (ctrl.error()) {
      return m('div.f3', [
        m(heading, { label: computeLabel(), class: 'f1', element: el, parentReinitialize: ctrl.parentReinitialize, dashboardCallback: ctrl.dashboardCallback }),
        m('div.red', ctrl.error())
      ]);
    }

    // isEditing already defined above
    const hiddenDs = ctrl.hiddenDatasets();
    const hiddenPts = ctrl.hiddenPoints();

    return m('div.f3', [
      m(heading, {
        label: computeLabel(),
        class: 'f1 mb1 ph1',
        element: el,
        parentReinitialize: ctrl.parentReinitialize,
        dashboardCallback: ctrl.dashboardCallback,
        // In browse mode, make the title clickable to the equipment detail page
        onClikLabel: (!isEditing && ctrl.targetEquipment()) ? function () {
          const eq = ctrl.targetEquipment();
          const eqClass = eq['@class'];
          const eqId = eq['@rid'] && eq['@rid'].toString().slice(1);
          if (eqClass && eqId) m.route.set(`/equipment/${eqClass}/${eqId}`);
        } : undefined,
        labelClass: (!isEditing && ctrl.targetEquipment()) ? 'blue pointer' : undefined
      }, [
        el.helpText ?
          m('i.ion-ios-information-outline.white.f1.ml2', {
            onclick: () => helpModal.editDialogue(el.name || 'Smart List', undefined, el.helpText)
          }) : ''
      ]),
      // Equipment picker moved to element edit modal; keep SmartList header clean
      (function () {
        const visibleDatasets = ctrl.datasets()
          .filter(ds => isEditing || !hiddenDs.has(ctrl.getDatasetKey(ds)));

        const datasetNodes = visibleDatasets.map(function (dataset, dsIdx) {
          const dsKey = ctrl.getDatasetKey(dataset);
          const datasetName = (dataset.name || '').trim();
          const isDefaultName = datasetName.toLowerCase() === 'default';
          const showDatasetTitle = !(labelStyle === 'first_dataset_name' && dsIdx === 0) && !isDefaultName;
          const headerRow = m('div.flex.justify-between.items-center', [
            m('div.f3.tl', [
              m('span.smartlist-dataset-handle', {
                class: isEditing ? 'blue' : '',
                style: { cursor: isEditing ? 'grab' : 'default' },
                onmousedown: e => { if (isEditing) e.currentTarget.style.cursor = 'grabbing'; },
                onmouseup:   e => { if (isEditing) e.currentTarget.style.cursor = 'grab'; },
                onmouseleave:e => { if (isEditing) e.currentTarget.style.cursor = 'grab'; }
              },
                showDatasetTitle ? datasetName : '')
            ]),
            isEditing ? m('i.blue.pointer.' + (hiddenDs.has(dsKey) ? 'ion-ios-circle-outline' : 'ion-ios-circle-filled') + '.mr1.f2', {
              onclick: function (e) { e.stopPropagation(); ctrl.toggleHideDataset(dataset); }
            }) : null
          ]);

          // Build display columns from dataset definition, then match to out_Column edges
          // Deduplicate any repeated names to avoid duplicated rows
          const defNamesRaw = Array.isArray(dataset.columns) && dataset.columns.length
            ? dataset.columns.map(c => c.name)
            : (dataset.out_Column || []).map(c => c.name);
          const defNames = ctrl.uniqueStable(defNamesRaw);

          // Apply saved column order for this dataset key
          const savedOrder = ctrl.order().points && ctrl.order().points[dsKey];
          let orderedNames = defNames.slice();
          if (Array.isArray(savedOrder) && savedOrder.length) {
            // Keep only unique names, prioritizing saved order, then any remaining from definition
            const result = [];
            const seen = new Set();
            for (const n of savedOrder) {
              if (defNames.includes(n) && !seen.has(n)) { seen.add(n); result.push(n); }
            }
            for (const n of defNames) {
              if (!seen.has(n)) { seen.add(n); result.push(n); }
            }
            orderedNames = result;
          }

          // Build a name -> list of edges map preserving server order
          const nameBuckets = {};
          (dataset.out_Column || []).forEach(edge => {
            if (!edge || !edge.name) return;
            if (!nameBuckets[edge.name]) nameBuckets[edge.name] = [];
            nameBuckets[edge.name].push(edge);
          });

          const columns = orderedNames
            // for each label, pick the first matching edge seen in server order
            .map(name => ({ name, columnRecord: (nameBuckets[name] && nameBuckets[name][0]) || undefined }))
            .filter(col => isEditing || !(hiddenPts[dsKey] && hiddenPts[dsKey].has(col.name)));

          const columnRows = columns.map(function (column, idx) {
            const ptHidden = hiddenPts[dsKey] && hiddenPts[dsKey].has(column.name);
            const isLast = idx === (columns.length - 1);
            // In edit mode, only show a single column with name + eye icon.
            // In browse mode, show name + the point value cell.
            // Always show bottom border on left cell
            const leftTdClass = 'narrow tl pl0 ' + (isEditing ? 'w-100' : 'w-50') + ' bb';
            return m('tr.smartlist-column', { key: column.name, id: column.name }, [
              m('td', { class: leftTdClass }, [
                m('div.flex.justify-between.items-center', [
                  m('span.smartlist-column-handle.f4', {
                    class: isEditing ? 'blue' : '',
                    style: { cursor: isEditing ? 'grab' : 'default' },
                    onmousedown: e => { if (isEditing) e.currentTarget.style.cursor = 'grabbing'; },
                    onmouseup:   e => { if (isEditing) e.currentTarget.style.cursor = 'grab'; },
                    onmouseleave:e => { if (isEditing) e.currentTarget.style.cursor = 'grab'; }
                  }, [
                    column.name || ''
                  ]),
                  isEditing ? m('i.blue.pointer.' + (ptHidden ? 'ion-ios-circle-outline' : 'ion-ios-circle-filled'), {
                    onclick: function (e) { e.stopPropagation(); ctrl.toggleHidePoint(dataset, column.name); }
                  }) : null
                ])
              ]),
              // Only render the point cell in browse mode
              !isEditing && column.columnRecord
                ? m(pointTd, { columnRecord: column.columnRecord, parentRid: ctrl.equipmentLocationRid || el.parentRid, readOnly: true, tdClass: '.bb.bl-0.pl2' })
                : (!isEditing ? m('td.bb.bl-0.pl1') : null)
            ]);
          });

          const rows = isEditing
            ? m(Sortable, {
              onsort: function (newOrder) {
                const order = ctrl.order();
                order.points = order.points || {};
                order.points[dsKey] = newOrder.map(item => item.id);
                ctrl.order(order);
                ctrl.saveOrder();
              },
              draggable: '.smartlist-column',
              handleClass: '.smartlist-column-handle',
              htmlElement: 'tbody'
            }, columnRows)
            : m('tbody', columnRows);

          return m('div.smartlist-dataset.b--mid-gray.bw1.mb2', { key: dsKey, id: dsKey }, [
            headerRow,
            m('table.w-100', { style: { tableLayout: 'fixed', borderCollapse: 'collapse', borderSpacing: 0 } }, [
              rows
            ])
          ]);
        });

        if (isEditing) {
          return m(Sortable, {
            onsort: function (newOrder) {
              const order = ctrl.order();
              order.datasets = newOrder.map(item => item.id);
              ctrl.order(order);
              // reorder datasets in memory
              const byKey = new Map(ctrl.datasets().map(d => [ctrl.getDatasetKey(d), d]));
              ctrl.datasets(order.datasets.map(k => byKey.get(k)).filter(Boolean));
              ctrl.saveOrder();
            },
            draggable: '.smartlist-dataset',
            handleClass: '.smartlist-dataset-handle',
            htmlElement: 'div'
          }, datasetNodes);
        }

        return m('div', datasetNodes);
      })()
    ]);
  }
};

module.exports = smartList;
